import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Hidden } from '@mui/material';
import Typography from '@ui/Typography';
import { StudioClipProps } from '../../StudioClip';
import { CompleteProps } from '.';
import useAudioPlayer from '../../../../lib/useAudioPlayer';
import Highlight from './Highlight';
import EditableTextLabel from '../../../Inputs/EditableTextLabel';
import { instanceOfCompletedClip } from '../../../../context/clips';

interface ClipBodyTopProps {
  clip: StudioClipProps['clip'];
  classes: CompleteProps['studioClipCompleteClasses']['classes'];
  playerState: ReturnType<typeof useAudioPlayer>[0]['state'];
  onNameChange: CompleteProps['onNameChange'];
  renderedError?: string | null;
  formattedPlaybackPosition: string;
  hideClipBodyTop?: boolean;
}

const ClipBodyTop = ({
  clip,
  classes,
  playerState,
  onNameChange,
  renderedError,
  formattedPlaybackPosition,
  hideClipBodyTop,
}: ClipBodyTopProps) => {
  const disabled: boolean = useMemo(
    () =>
      !!clip.failedWithError ||
      Boolean(instanceOfCompletedClip(clip) && clip.highlights),
    [clip]
  );

  if (hideClipBodyTop) {
    return renderedError ? (
      <div className={classes.errorContainer}>
        <Typography role="alert" variant="pSm" color="error">
          {renderedError}
        </Typography>
      </div>
    ) : null;
  }

  return (
    <div className={classes.clipBodyTop}>
      <Highlight clip={clip} field="name" className={classes.highlight}>
        {typeof onNameChange === 'function' ? (
          <EditableTextLabel
            id={`studio-clip-${clip.id}`}
            disabled={disabled}
            ariaLabel={`Clip name`}
            value={clip.name}
            tooltipTitle={'Edit clip name'}
            onChange={onNameChange}
          />
        ) : (
          <Typography className={clsx(classes.clipNameText)} variant="pMd">
            {clip.name}
          </Typography>
        )}
      </Highlight>
      <Hidden smDown={!!renderedError}>
        <div className={classes.clipTextPreviewContainer}>
          <Highlight
            clip={clip}
            className={clsx(classes.clipTextPreview, classes.highlight)}
            field="text"
          >
            <Typography
              noWrap
              color="tertiary"
              className={clsx(classes.clipTextPreview)}
              variant="pMd"
            >
              {clip.text}
            </Typography>
          </Highlight>
        </div>
      </Hidden>
      {!!renderedError && (
        <div className={classes.errorContainer}>
          <Typography role="alert" variant="pSm" color="error">
            {renderedError}
          </Typography>
        </div>
      )}
      {!clip.failedWithError &&
        playerState !== 'ERROR' &&
        playerState !== 'LOADING' && (
          <Hidden smDown>
            <div className={classes.playbackPositionContainer}>
              <Typography variant="pSm" color="secondary">
                {formattedPlaybackPosition}
              </Typography>
            </div>
          </Hidden>
        )}
    </div>
  );
};

export default ClipBodyTop;
